// ======================================================
// LexCallAngelApp.jsx — 20 Jun 2025  (≈ 880 lines)
// 依赖：npm i city-timezones
// ======================================================

import React, { useState, useEffect } from 'react';
import {
  Routes, Route, NavLink, useParams,
  useNavigate, useLocation,
} from 'react-router-dom';
import {
  FiPhoneCall, FiPlus, FiUsers, FiFileText,
  FiMic, FiClock,
} from 'react-icons/fi';
import cityTz from 'city-timezones';
import './LexCallAngel.css';    // ← 样式文件同之前
import AngelWrapper from './angel';  // ← 新增

/* Firebase：当前用户 + 读/写助手 --------------------------- */
import { auth, loadLcaData, saveLcaData } from '../firebase';

/* ---------- utils ---------- */
const uuid = () => `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
const tzByCity = c => cityTz.lookupViaCity(c)[0]?.timezone || 'UTC';

const COUNTRY_CODES = [
  { cc: '+1',  label: '🇺🇸 +1'  },
  { cc: '+44', label: '🇬🇧 +44' },
  { cc: '+86', label: '🇨🇳 +86' },
  { cc: '+61', label: '🇦🇺 +61' },
];

/* ---------- Context ---------- */
const AppCtx = React.createContext();

function AppProvider({ children }) {
  /* ── local state ───────────────────────────────────── */
  const [autoCalls, setAutoCalls] = useState([]);
  const [contacts,  setContacts]  = useState([]);
  const [notes,     setNotes]     = useState([{ id: uuid(), text: 'Daily agenda' }]);
  const hasSelf = contacts.length > 0;

  /* ── CRUD helpers (in-memory) ─────────────────────── */
  const addCall    = c      => setAutoCalls(l => [...l, { ...c, id: uuid() }]);
  const updateCall = (id,u) => setAutoCalls(l => l.map(c => c.id === id ? { ...c, ...u } : c));
  const deleteCall = id     => setAutoCalls(l => l.filter(c => c.id !== id));

  const saveContact = (name, cc, phone) => {
    if (!contacts.find(c => c.cc === cc && c.phone === phone))
      setContacts(l => [...l, { id: uuid(), name, cc, phone }]);
  };

  /* ── Firestore ⬆⬇ 同步（登录后才启用） ───────────── */
  useEffect(() => {
    if (!auth.currentUser) return;                          // 未登录
    loadLcaData().then(data => {
      if (data) {
        setAutoCalls(data.autoCalls || []);
        setContacts(data.contacts  || []);
        setNotes    (data.notes     || []);
      }
    });
  }, [auth.currentUser?.uid]);

  /* 本地变更 → 500 ms debounce 写回云端 */
  useEffect(() => {
    if (!auth.currentUser) return;
    const t = setTimeout(() => {
      saveLcaData({ autoCalls, contacts, notes })
        .catch(err => console.error('❌ saveLcaData', err));
    }, 500);
    return () => clearTimeout(t);
  }, [autoCalls, contacts, notes, auth.currentUser?.uid]);

  return (
    <AppCtx.Provider value={{
      autoCalls, contacts, notes,
      addCall, updateCall, deleteCall,
      saveContact, setNotes, hasSelf,
    }}>
      {children}
    </AppCtx.Provider>
  );
}

/* ---------- Layout & Bottom Tabs ---------- */
function Layout() {
  return (
    <div className="lca-layout">
      <BottomTabBar />
      <main className="lca-content">
        <Routes>
          <Route index           element={<HomePage />} />
          <Route path="create"   element={<CreatePage />} />
          <Route path="edit/:id" element={<EditPage />} />
          <Route path="contacts" element={<ContactsPage />} />
          <Route path="notes"    element={<NotesPage />} />
        </Routes>
      </main>
      
    </div>
  );
}
function BottomTabBar() {
  const tabs = [
    ['',          FiPhoneCall, 'Auto Calls'],
    ['contacts',  FiUsers,     'Contacts' ],
    ['notes',     FiFileText,  'Notes'    ],
  ];
  return (
    <nav className="lca-tabbar">
      {tabs.map(([to,Icon,label]) => (
        <NavLink key={to} to={to} end={to===''}
          className={({isActive}) => isActive ? 'lca-tab active' : 'lca-tab'}>
          <Icon /><span>{label}</span>
        </NavLink>
      ))}
    </nav>
  );
}



export default function LexCallAngelRoot() {

  return (
    <AngelWrapper>
      <AppProvider>
        <Layout />
      </AppProvider>
    </AngelWrapper>
  );
}

/* ---------- Guard：无联系人则强制跳 Contacts ---------- */
function useSelfGuard() {

}

/* ────────────────── 共用小组件 ────────────────── */
const DowPicker = ({ value, onChange }) => {
  const D = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];
  return (
    <div className="dow-grid">
      {D.map((d,i)=>(
        <button key={d} type="button"
          className={value.includes(i)?'dow on':'dow'}
          onClick={()=>onChange(value.includes(i)?value.filter(v=>v!==i):[...value,i])}>
          {d}
        </button>
      ))}
    </div>
  );
};

const Recorder = ({ onSave }) => {
  const [rec,setRec] = useState(null);
  const [url,setUrl] = useState('');
  const start = async () => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio:true });
    const r = new MediaRecorder(stream); const chunks=[];
    r.ondataavailable = e => chunks.push(e.data);
    r.onstop = () => {
      const blob = new Blob(chunks,{type:'audio/webm'});
      const u = URL.createObjectURL(blob);
      setUrl(u); onSave(blob,u);
    };
    r.start(); setRec(r);
  };
  return (
    <>
      {rec
        ? <button className="lca-btn danger mt-16" onClick={()=>rec.stop()}>Stop</button>
        : <button className="lca-btn primary mt-16" onClick={start}><FiMic/> Record</button>}
      {url && <div className="mt-16"><audio controls src={url}/></div>}
    </>
  );
};

/* ---------- 通用 CallForm（Create / Edit 共用） ---------- */
function CallForm({ initial={}, contacts, notes, onSubmit, onCancel }) {
  const [form, set] = useState({
    topic:'',                       // ★ 场景标题
    contactId: contacts[0]?.id || '',
    city:'', tz:'UTC', time:'08:00',
    repeat:[], msgType:'text', note:'', audio:null,
    ...initial,
  });

  /* handlers */
  const u = e => set({ ...form, [e.target.name]: e.target.value });
  const pickNote = e => {
    const n = notes.find(n=>n.id===e.target.value);
    set(f=>({...f,note:n?.text||''}));
  };
  const onRec = (blob) => set(f=>({...f,audio:blob}));

  return (
    <div className="lca-form">
      {/* Topic */}
      <label>Topic</label>
      <input
        name="topic"
        placeholder="Morning Wake-up Call, Call Mom on Birthday, Call Clients with my notes... "
        value={form.topic}
        onChange={u}
      />

      {/* Contact */}
      <label className="mt-24">Contact</label>
      <select name="contactId" value={form.contactId} onChange={u}>
        {contacts.map(c=>(
          <option key={c.id} value={c.id}>
            {c.name} ({c.cc}{c.phone})
          </option>
        ))}
      </select>

      {/* City → Time-zone */}
      <label className="mt-24">City</label>
      <input
        name="city"
        value={form.city}
        onChange={e=>set(f=>({...f,city:e.target.value,tz:tzByCity(e.target.value)}))}
        placeholder="e.g. London"
      />
      <p className="lca-card-note">Time-zone: {form.tz}</p>

      {/* Time */}
      <label>Time</label>
      <div className="time-row"
           onClick={()=>document.getElementById('timeInp').showPicker?.()}>
        <FiClock className="time-ic"/>
        <input id="timeInp" name="time" type="time" value={form.time} onChange={u}/>
      </div>

      {/* Repeat */}
      <label>Repeat Days</label>
      <DowPicker value={form.repeat} onChange={r=>set(f=>({...f,repeat:r}))}/>

      {/* Message type */}
      <label className="mt-24">Message Type</label>
      <select name="msgType" value={form.msgType} onChange={u}>
        <option value="text">Text → TTS</option>
        <option value="audio">Upload Audio</option>
        <option value="record">Record Voice</option>
        <option value="note">Pick Note</option>
      </select>

      {form.msgType==='text' && (
        <textarea
          name="note"
          value={form.note}
          onChange={u}
          placeholder="Write text…"
        />
      )}
      {form.msgType==='audio' && (
        <input
          type="file"
          accept="audio/*"
          onChange={e=>set(f=>({...f,audio:e.target.files[0]}))}
        />
      )}
      {form.msgType==='record' && <Recorder onSave={onRec}/>}

      {form.msgType==='note' && (
        <>
          <select onChange={pickNote} defaultValue="">
            <option value="" disabled>Select a note</option>
            {notes.map(n=>(
              <option key={n.id} value={n.id}>{n.text.slice(0,30)}</option>
            ))}
          </select>
          {form.note && <p className="lca-card-note mt-8">✔ chosen.</p>}
        </>
      )}

      {/* action btns */}
      <button className="lca-btn primary mt-24" onClick={()=>onSubmit(form)}>Save</button>
      <button className="lca-btn" onClick={onCancel}>Cancel</button>
    </div>
  );
}

/* ──────────────────── Pages ──────────────────── */

function HomePage() {
  useSelfGuard();
 const { autoCalls, hasSelf } = React.useContext(AppCtx);

  const nav = useNavigate();
  return (
    <section className="lca-section">
      <header className="lca-section-header">
        <h2> LEX Call Angel<br />
        Hi, this is - 888 884 5281 
        <br /> Your calls on autopilot </h2>
        <button className="lca-iconbtn" onClick={()=>nav('create')}>
          <FiPlus/>
        </button>
      </header>

{/* ✅ 插入这个 */}
      {!hasSelf && (
        <div className="lca-empty" style={{ marginBottom: '24px' }}>
          <p style={{ color: 'white', fontWeight: 'bold', marginBottom: '1em' }}>
            Add a new contact ↓
          </p>
          <button className="lca-btn primary" onClick={() => nav('contacts')}>
            Yes
          </button>
        </div>
      )}






      {autoCalls.length===0
        ? <div className="lca-empty">

<p style={{ textAlign: 'center', color: 'white' }}>
  
   <code style={{ display: 'block', textAlign: 'left', marginLeft: '1.25em', color: 'white', fontWeight:800 }}>
    Use Cases：
  </code>
  <code style={{ display: 'block', textAlign: 'left', marginLeft: '1.25em', color: 'white' }}>
    • You can ask Lex to call you in the morning with your action items,
  </code>
  <code style={{ display: 'block', textAlign: 'left', marginLeft: '1.25em', color: 'white' }}>
    • wake your son up for school with a pep talk, 
  </code>
  <code style={{ display: 'block', textAlign: 'left', marginLeft: '1.25em', color: 'white' }}>
    • call your mom on her birthday using your own voice,
  </code>
  <code style={{ display: 'block', textAlign: 'left', marginLeft: '1.25em', color: 'white' }}>
    • call clients with your notes, 
  </code>
  <code style={{ display: 'block', textAlign: 'left', marginLeft: '1.25em', color: 'white' }}>
    • remind you to take a break, 
  </code>
  <code style={{ display: 'block', textAlign: 'left', marginLeft: '1.25em', color: 'white' }}>
    • or even call you “Angel” so you can fly with Lex.
  </code>
</p>


</div>

        : autoCalls.map(c => <CallCard key={c.id} call={c}/>)}
      
    </section>
  );
}
const CallCard = ({ call }) => {
  const nav = useNavigate();
  return (
    <div className="lca-card" onClick={()=>nav(`edit/${call.id}`)}>
      <div className="lca-card-row">
        <span className="lca-card-date">
          {call.repeat?.length ? `⟳ ${call.repeat.length}d` : 'Once'}
        </span>
        <span className="lca-card-time">{call.time}</span>
      </div>
      <div className="lca-card-title">{call.topic || 'Untitled'}</div>
      <div className="lca-card-note">
        Call to <strong>{call.contactName}</strong>
      </div>
      {call.note && <div className="lca-card-note">{call.note}</div>}
    </div>
  );
};

function CreatePage() {
  useSelfGuard();
  const { contacts, notes, addCall } = React.useContext(AppCtx);
  const nav = useNavigate();

  const submit = data => {
    const ct = contacts.find(c=>c.id===data.contactId);
    if (!ct) { alert('Select contact'); return; }
    addCall({ ...data, contactName: ct.name });
    nav('/lexcallangel');
  };

  return (
    <section className="lca-section">
      <header className="lca-section-header"><h2>New Call Angel</h2></header>
      <CallForm
        contacts={contacts}
        notes={notes}
        onSubmit={submit}
        onCancel={()=>nav(-1)}
      />
    </section>
  );
}

function EditPage() {
  useSelfGuard();
  const { id } = useParams();
  const { autoCalls, contacts, notes, updateCall, deleteCall } = React.useContext(AppCtx);
  const nav = useNavigate();
  const call = autoCalls.find(c=>c.id===id);
  if (!call) return <p>Not found</p>;

  const save = data => { updateCall(id,data); nav(-1); };

  return (
    <section className="lca-section">
      <header className="lca-section-header">
        <h2>Edit Call</h2>
        <button className="lca-btn danger"
          onClick={()=>{ deleteCall(id); nav('/lexcallangel'); }}>
          Delete
        </button>
      </header>
      <CallForm
        initial={call}
        contacts={contacts}
        notes={notes}
        onSubmit={save}
        onCancel={()=>nav(-1)}
      />
    </section>
  );
}

function ContactsPage() {
  const { contacts, saveContact, hasSelf } = React.useContext(AppCtx);
  const nav = useNavigate(), loc = useLocation();
  const [d,set] = useState({ name:'', cc:'+1', phone:'' });
  const on = e => set({ ...d, [e.target.name]: e.target.value });
  const add=()=> { if(d.name && d.phone) saveContact(d.name,d.cc,d.phone); set({...d, name:'', phone:''}); };
  useEffect(()=>{ if(loc.state?.first && hasSelf) nav('/lexcallangel',{replace:true}); },[hasSelf]);

  return (
    <section className="lca-section">
      <header className="lca-section-header">
        <h2>{loc.state?.first ? 'First-time Setup' : 'Contacts'}</h2>
      </header>
      <div className="lca-form">
        <label>Name</label><input name="name" value={d.name} onChange={on}/>
        <label>Country Code</label>
        <select name="cc" value={d.cc} onChange={on}>
          {COUNTRY_CODES.map(o=><option key={o.cc} value={o.cc}>{o.label}</option>)}
        </select>
        <label>Phone</label><input name="phone" value={d.phone} onChange={on}/>
        <button className="lca-btn primary" onClick={add}>Add</button>
      </div>

      {contacts.map(c=>(
        <div key={c.id} className="lca-card contact">
          <div className="lca-card-row"><span>{c.name}</span><span>{c.cc}{c.phone}</span></div>
        </div>
      ))}
    </section>
  );
}

function NotesPage() {
  const { notes, setNotes } = React.useContext(AppCtx);
  const [txt,setTxt] = useState('');
  const add=()=>{ if(txt.trim()){ setNotes(l=>[...l,{id:uuid(),text:txt}]); setTxt(''); } };

  return (
    <section className="lca-section">
      <header className="lca-section-header"><h2>Notes</h2></header>
      <div className="lca-form">
        <textarea
          value={txt}
          onChange={e=>setTxt(e.target.value)}
          placeholder="Write note…"
        />
        <button className="lca-btn primary" onClick={add}>Save Note</button>
      </div>

      {notes.map(n=>(
        <div key={n.id} className="lca-card">{n.text}</div>
      ))}
    </section>
  );
}
